'use client'

import { useState, useMemo, useEffect } from 'react'
import { Check, ChevronsUpDown, Clock } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useSchools, School } from '@/lib/hooks/use-schools'
import { useMySchools } from '@/lib/hooks/use-my-schools'
import { useRecentSchools } from '@/lib/hooks/use-recent-schools'
import { useUser, useAuth } from '@clerk/nextjs'
import { usePathname, useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { isSuperAdminFromClient } from '@/lib/utils/roles'
import { getSchoolLogoUrl, getSchoolInitials } from '@/lib/utils/school-logo'
import { pathAfterSchoolSwitch } from '@/lib/utils/school-scoped-path'
import { useTheme } from 'next-themes'
import { useLeaveGuard } from '@/components/context/leave-guard-context'

interface DisplaySchool {
  schoolId: string
  name: string
  slug: string
  imageUrl: string
}

export function SchoolSwitcher() {
  const { isLoaded: isUserLoaded } = useUser()
  const authResult = useAuth()
  const { schools: allSchools, isLoading: isLoadingAllSchools, currentSchool, changeSchool } = useSchools()
  const { schoolMemberships, isLoading: isLoadingMemberships } = useMySchools()
  const { addToRecentSchools, recentSchools } = useRecentSchools()
  const { confirmLeave } = useLeaveGuard()
  const { theme } = useTheme()
  const router = useRouter()
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Determine if the current user is a super admin from session claims
  const isSuperAdmin = isSuperAdminFromClient(authResult)

  const isLoaded = mounted && isUserLoaded && !isLoadingMemberships

  // Determine which schools to display based on user type
  const displaySchools = useMemo(() => {
    if (!isLoaded) return []

    let schools: DisplaySchool[] = []

    if (isSuperAdmin && Array.isArray(allSchools)) {
      // Super admins see all schools from the platform
      schools = allSchools.map(s => ({
        schoolId: s.id,
        name: s.name,
        slug: (s as any).slug ?? '',
        imageUrl: ''
      }))
    } else {
      // Regular users see schools from their memberships
      schools = schoolMemberships
        .filter(m => m.school && m.status === 'ACTIVE')
        .map(m => ({
          schoolId: m.school!.id,
          name: m.school!.name,
          slug: m.school!.slug ?? '',
          imageUrl: ''
        }))
    }

    // For super admins, prioritize recent schools
    if (isSuperAdmin && recentSchools.length > 0) {
      const recentSchoolIds = recentSchools.slice(0, 3).map(rs => rs.id)
      const recentSchoolsList = schools.filter(s => recentSchoolIds.includes(s.schoolId))
      const otherSchools = schools.filter(s => !recentSchoolIds.includes(s.schoolId))

      const sortedRecentSchools = recentSchoolsList.sort((a, b) => {
        const aIndex = recentSchoolIds.indexOf(a.schoolId)
        const bIndex = recentSchoolIds.indexOf(b.schoolId)
        return aIndex - bIndex
      })

      const sortedOtherSchools = otherSchools.sort((a, b) => a.name.localeCompare(b.name))
      return [...sortedRecentSchools, ...sortedOtherSchools]
    }

    return [...schools].sort((a, b) => a.name.localeCompare(b.name))
  }, [isSuperAdmin, allSchools, schoolMemberships, isLoaded, recentSchools])

  const hasOnlyOneSchool = displaySchools.length === 1

  // Candidate list for auto-select / cookie-write. Unlike `displaySchools`
  // (which filters regular users' memberships to `status === 'ACTIVE'` for the
  // dropdown), this includes PENDING memberships. A brand-new school's first
  // admin is onboarded as PENDING, so an ACTIVE-only list is empty for them and
  // `changeSchool` (the cookie writer) never fires — leaving every school-bound
  // request without `x-school-id` and stuck on HTTP 428. Drive auto-select off
  // the unfiltered membership set, independent of what the dropdown displays.
  // ACTIVE memberships are ordered first so a returning multi-school admin
  // (whose schoolUsers ordering is not ACTIVE-first) isn't demoted onto a
  // PENDING school; the PENDING fallback only kicks in when no ACTIVE exists.
  const autoSelectSchools = useMemo<DisplaySchool[]>(() => {
    if (!isLoaded) return []
    if (isSuperAdmin) return displaySchools
    const withSchool = schoolMemberships.filter(m => m.school)
    const ordered = [...withSchool.filter(m => m.status === 'ACTIVE'), ...withSchool.filter(m => m.status !== 'ACTIVE')]
    return ordered.map(m => ({
      schoolId: m.school!.id,
      name: m.school!.name,
      slug: m.school!.slug ?? '',
      imageUrl: ''
    }))
  }, [isLoaded, isSuperAdmin, displaySchools, schoolMemberships])

  // Auto-select the first school if none is selected, or re-select if stored school is invalid
  useEffect(() => {
    if (!isLoaded || !autoSelectSchools.length) return

    // If current school is set, validate it's still in the user's schools
    if (currentSchool) {
      const isValid = autoSelectSchools.some(s => s.schoolId === currentSchool.id)
      if (isValid) return // All good
      // Stale school — fall through to auto-select
    }

    const first = autoSelectSchools[0]
    changeSchool({
      id: first.schoolId,
      name: first.name,
      createdAt: '',
      updatedAt: ''
    } as School)
  }, [isLoaded, currentSchool, autoSelectSchools, changeSchool])

  if (!isLoaded) {
    return (
      <Button variant="ghost" className="w-[300px] justify-start">
        <Avatar className="h-7 w-7">
          <AvatarFallback>...</AvatarFallback>
        </Avatar>
      </Button>
    )
  }

  if (hasOnlyOneSchool) {
    return (
      <div className="flex items-center gap-2 px-3">
        <Avatar className="h-7 w-7">
          <AvatarImage
            src={getSchoolLogoUrl(currentSchool, theme, true) || '/img/logo/bb-logo-sm.png'}
            alt={`${currentSchool?.name || 'School'} logo`}
          />
          <AvatarFallback>{getSchoolInitials(currentSchool?.name)}</AvatarFallback>
        </Avatar>
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold truncate">
            {currentSchool?.name || displaySchools[0]?.name || 'School'}
          </span>
        </div>
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          aria-label="Select a school"
          className="justify-start px-3 gap-2"
        >
          <Avatar className="h-7 w-7">
            <AvatarImage
              src={getSchoolLogoUrl(currentSchool, theme, true) || '/img/logo/bb-logo-sm.png'}
              alt={`${currentSchool?.name || 'School'} logo`}
            />
            <AvatarFallback>{getSchoolInitials(currentSchool?.name)}</AvatarFallback>
          </Avatar>
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-start truncate">
              <span className="text-sm font-semibold truncate w-full">{currentSchool?.name || 'Select School'}</span>
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search schools..." />
          <CommandList>
            <CommandEmpty>No schools found.</CommandEmpty>
            <CommandGroup>
              {displaySchools.map((school: DisplaySchool) => {
                const isRecent = isSuperAdmin && recentSchools.slice(0, 3).some(rs => rs.id === school.schoolId)

                return (
                  <CommandItem
                    key={school.schoolId}
                    value={school.name}
                    onSelect={() => {
                      // A school-keyed editor with unsaved work remounts under the
                      // new school and drops its draft, and nothing else warns it.
                      // Cancelling keeps the school, the page and the draft.
                      if (school.schoolId !== currentSchool?.id && !confirmLeave()) {
                        setOpen(false)
                        return
                      }

                      // Prefer the full school record (with logos) from the
                      // fetched list so post-reload hydration has everything
                      // it needs to render the avatar immediately.
                      const fullSchool = Array.isArray(allSchools)
                        ? allSchools.find((s: School) => s.id === school.schoolId)
                        : undefined
                      const selectedSchool = (fullSchool ?? {
                        id: school.schoolId,
                        name: school.name,
                        createdAt: '',
                        updatedAt: ''
                      }) as School

                      // Writes the active-school cookie synchronously before it
                      // awaits, so the navigation below already carries the new
                      // `x-school-id`.
                      changeSchool(selectedSchool)

                      if (isSuperAdmin) {
                        addToRecentSchools(selectedSchool)
                      }

                      setOpen(false)

                      // A record id in the URL belongs to the school we just
                      // left, so staying put would read a record the new school
                      // can't see. Step up to that section's list instead of
                      // letting the detail page render its not-found state.
                      const destination = pathAfterSchoolSwitch(pathname)
                      if (destination) {
                        toast.info(`That page isn't available for ${school.name}`)
                        router.replace(destination)
                      }
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        currentSchool?.id === school.schoolId ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex items-center justify-between w-full">
                      <span>{school.name}</span>
                      {isRecent && <Clock className="h-3 w-3 text-muted-foreground" />}
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
